import { useCallback, useEffect, useRef, useState } from 'react';
import { listRestaurantInventoryItemsPage } from '../Api/Inventory.api';
import { restaurantInventoryRowToDraft, normalizeItemRows } from '../Utils';
import type { InventoryItemDraft } from '../Types';

const PAGE_SIZE = 50;

export const useSavedInventoryItems = (
  sessionToken: string | null,
  branchId: string | undefined,
  enabled: boolean
) => {
  const [items, setItems] = useState<InventoryItemDraft[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  const load = useCallback(async () => {
    if (!enabled || !sessionToken || !branchId) {
      setItems(null);
      setTotal(null);
      setHasMore(false);
      pageRef.current = 1;
      return;
    }

    setLoading(true);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    pageRef.current = 1;
    try {
      const page = await listRestaurantInventoryItemsPage({
        accessToken: sessionToken,
        branchId,
        page: 1,
        limit: PAGE_SIZE
      });

      const drafts = normalizeItemRows(
        page.data.map(restaurantInventoryRowToDraft)
      );
      setItems(drafts);

      if (page.meta) {
        setTotal(page.meta.total);
        setHasMore(Boolean(page.meta.has_more));
      } else {
        // Legacy full-list response (no pagination meta).
        setTotal(drafts.length);
        setHasMore(false);
      }
    } catch {
      setItems(normalizeItemRows([]));
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [branchId, enabled, sessionToken]);

  const loadMore = useCallback(async () => {
    if (
      !enabled ||
      !sessionToken ||
      !branchId ||
      !hasMore ||
      loadingMoreRef.current ||
      loading
    ) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const nextPage = pageRef.current + 1;
      const page = await listRestaurantInventoryItemsPage({
        accessToken: sessionToken,
        branchId,
        page: nextPage,
        limit: PAGE_SIZE
      });

      const drafts = page.data.map(restaurantInventoryRowToDraft);
      setItems(prev =>
        normalizeItemRows([...(prev ?? []).filter(r => r.name.trim()), ...drafts])
      );
      pageRef.current = nextPage;

      if (page.meta) {
        setTotal(page.meta.total);
        setHasMore(Boolean(page.meta.has_more));
      } else {
        setHasMore(false);
      }
    } catch {
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [branchId, enabled, hasMore, loading, sessionToken]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    items,
    total,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    reload: load,
    setItems
  };
};
